/**
 * =============================================================================
 * SPEQ55 RFP Pro - RFP Analyze Configuration Page
 * =============================================================================
 *
 * Page for configuring and starting an RFP analysis.
 * Allows selection of keywords, technical specs, and extended mode.
 *
 * @module pages/rfps/RFPAnalyze
 * @author SPEQ55
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { get } from '../../api/client';
import {
  getAnalysisDefaults,
  startAnalysis,
  listAnalyses,
  downloadAnalysisPDF,
  UsageLimitError,
} from '../../api/analysisApi';
import { listParameterSets } from '../../api/parameterSetsApi';
import { getMyUsage } from '../../api/usageApi';
import KeywordSelector from '../../components/analysis/KeywordSelector';
import SpecSelector from '../../components/analysis/SpecSelector';
import UsageMeter from '../../components/usage/UsageMeter';

const VISIBILITY_LABELS = {
  org_default: 'Organization Defaults',
  shared: 'Shared in Organization',
  private: 'My Private Sets',
};

function RFPAnalyze() {
  const { id } = useParams();
  const navigate = useNavigate();

  // State
  const [rfp, setRfp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Analysis configuration
  const [availableKeywords, setAvailableKeywords] = useState([]);
  const [availableSpecs, setAvailableSpecs] = useState([]);
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [selectedSpecs, setSelectedSpecs] = useState([]);
  const [extendedMode, setExtendedMode] = useState(false);

  // Parameter set dropdown state
  const [parameterSets, setParameterSets] = useState([]);
  const [selectedParameterSetId, setSelectedParameterSetId] = useState('');

  // Completed analyses
  const [completedAnalyses, setCompletedAnalyses] = useState([]);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  // Usage tracking
  const [usage, setUsage] = useState(null);
  const [usageLimitError, setUsageLimitError] = useState(null);

  /**
   * Load usage statistics.
   */
  const loadUsage = async () => {
    try {
      const usageData = await getMyUsage();
      setUsage(usageData);
    } catch {
      // Silently fail - usage meter will not display
    }
  };

  /**
   * Load RFP and defaults on mount.
   */
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Load RFP details, defaults, parameter sets, existing analyses in parallel
        const [rfpResponse, defaults, sets, analyses] = await Promise.all([
          get(`/rfps/${id}`),
          getAnalysisDefaults(id),
          listParameterSets().catch(() => []),
          listAnalyses(id),
        ]);

        // Load usage separately (non-critical)
        loadUsage();

        const rfpData = rfpResponse.data.rfp;
        setRfp(rfpData);

        // Check if RFP has a file
        if (!rfpData.filePath) {
          setError('This RFP does not have an uploaded file. Please upload a PDF first.');
        }

        // Set available options from defaults (org default set or hardcoded fallback)
        setAvailableKeywords(defaults.keywords || []);
        setAvailableSpecs(defaults.technicalSpecs || []);

        // Pre-select all items from the default set
        setSelectedKeywords(defaults.keywords || []);
        setSelectedSpecs(defaults.technicalSpecs || []);

        // Populate parameter set dropdown and pre-select the one backing
        // the defaults response (if any).
        setParameterSets(Array.isArray(sets) ? sets : []);
        if (defaults.parameterSetId) {
          setSelectedParameterSetId(defaults.parameterSetId);
        }

        // Set completed analyses (only show completed ones)
        setCompletedAnalyses(analyses.filter((a) => a.status === 'completed') || []);
      } catch (err) {
        if (err.response?.status === 404) {
          setError('RFP not found');
        } else if (err.response?.status === 403) {
          setError('You do not have permission to analyze this RFP');
        } else {
          setError('Failed to load RFP data');
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  /**
   * Close usage limit error modal.
   */
  const closeUsageLimitModal = () => {
    setUsageLimitError(null);
  };

  /**
   * Apply a parameter set to the active keyword/spec selection. Edits made
   * via the KeywordSelector / SpecSelector after this call are ephemeral —
   * the source set is never modified.
   */
  const applyParameterSet = (setId) => {
    setSelectedParameterSetId(setId);
    if (!setId) {
      return;
    }
    const set = parameterSets.find((s) => s.id === setId);
    if (!set) {
      return;
    }
    const nextKeywords = set.keywords || [];
    const nextSpecs = set.technicalSpecs || set.technical_specs || [];
    setAvailableKeywords(nextKeywords);
    setAvailableSpecs(nextSpecs);
    setSelectedKeywords(nextKeywords);
    setSelectedSpecs(nextSpecs);
  };

  /**
   * Re-apply the currently selected set (discarding ephemeral edits).
   */
  const resetToSelectedSet = () => {
    if (selectedParameterSetId) {
      applyParameterSet(selectedParameterSetId);
    }
  };

  /**
   * Groups parameter sets into dropdown optgroups by visibility.
   */
  const groupedParameterSets = parameterSets.reduce((acc, set) => {
    const key = set.visibility || 'private';
    if (!acc[key]) acc[key] = [];
    acc[key].push(set);
    return acc;
  }, {});

  /**
   * Handle PDF download for a completed analysis.
   */
  const handleDownloadPdf = async (analysisId) => {
    setDownloadingPdf(analysisId);
    try {
      await downloadAnalysisPDF(id, analysisId, rfp?.rfpNumber);
    } catch {
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(null);
    }
  };

  /**
   * Handle form submission.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setUsageLimitError(null);

    try {
      const analysis = await startAnalysis(id, {
        keywords: selectedKeywords,
        technicalSpecs: selectedSpecs,
        extendedMode,
        parameterSetId: selectedParameterSetId || null,
      });

      // Refresh usage stats after successful analysis start
      loadUsage();

      // Navigate to results page
      navigate(`/app/rfps/${id}/analysis/${analysis.id}`);
    } catch (error) {
      // Handle usage limit error specifically
      if (error instanceof UsageLimitError) {
        setUsageLimitError(error);
      } else {
        setError(error.response?.data?.error || 'Failed to start analysis. Please try again.');
      }
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin mx-auto" />
          <p className="mt-4 text-accent-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state (no RFP loaded)
  if (error && !rfp) {
    return (
      <div className="space-y-6">
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
        <Link to="/app/rfps" className="btn-outline">
          Back to RFPs
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <Link to="/app/rfps" className="text-accent-500 hover:text-accent-700">
            RFPs
          </Link>
          <span className="text-accent-400">/</span>
          <Link to={`/app/rfps/${id}`} className="text-accent-500 hover:text-accent-700">
            {rfp?.rfpNumber}
          </Link>
          <span className="text-accent-400">/</span>
          <span className="text-accent-800">Analyze</span>
        </div>
        <h1 className="text-2xl font-bold text-accent-800">RFP Analysis</h1>
        <p className="mt-1 text-accent-500">{rfp?.title}</p>
      </div>

      {/* Usage Meter */}
      {usage && <UsageMeter count={usage.count} limit={usage.limit} resetsAt={usage.resetsAt} />}

      {/* Error Alert */}
      {error && (
        <div className="bg-error-light border-l-4 border-error p-4 rounded">
          <p className="text-error-dark">{error}</p>
        </div>
      )}

      {/* File Info */}
      {rfp?.filePath && (
        <div className="card">
          <div className="card-body flex items-center gap-4">
            <div className="flex-shrink-0">
              <svg className="w-10 h-10 text-error" fill="currentColor" viewBox="0 0 24 24">
                <path d="M14,2H6C4.9,2,4,2.9,4,4v16c0,1.1,0.9,2,2,2h12c1.1,0,2-0.9,2-2V8L14,2z M14,8V3.5L18.5,8H14z M16,16H8v-2h8V16z M16,12H8v-2h8V12z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-medium text-accent-800">
                {rfp?.originalFilename || 'Uploaded PDF'}
              </p>
              <p className="text-sm text-accent-500">
                {rfp?.fileSize ? `${(rfp.fileSize / 1024 / 1024).toFixed(2)} MB` : 'PDF Document'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Completed Analyses */}
      {completedAnalyses.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Completed Analyses</h2>
            <p className="text-sm text-accent-500 mt-1">
              Previously completed analyses for this RFP. Download includes summary, parameters, and
              Q&A transcript.
            </p>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {completedAnalyses.map((analysis) => (
                <div
                  key={analysis.id}
                  className="flex items-center justify-between p-3 bg-accent-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-8 h-8 text-primary-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <div>
                      <p className="font-medium text-accent-800">
                        Analysis - {new Date(analysis.completedAt).toLocaleDateString()}
                      </p>
                      <p className="text-sm text-accent-500">
                        {analysis.extendedMode ? 'Extended Analysis' : 'Normal Analysis'} |{' '}
                        {analysis.technicalParameters?.length || 0} parameters
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/app/rfps/${id}/analysis/${analysis.id}`}
                      className="btn-outline text-sm py-1.5 px-3"
                    >
                      View
                    </Link>
                    <button
                      onClick={() => handleDownloadPdf(analysis.id)}
                      disabled={downloadingPdf === analysis.id}
                      className="btn-primary text-sm py-1.5 px-3 flex items-center gap-1"
                    >
                      {downloadingPdf === analysis.id ? (
                        <>
                          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                          Downloading...
                        </>
                      ) : (
                        <>
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                          PDF
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Analysis Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Parameter Set Selector */}
        {parameterSets.length > 0 && (
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold text-accent-800">Parameter Set</h2>
              <p className="text-sm text-accent-500 mt-1">
                Choose a reusable set of keywords and technical specs. Edits below are only for this
                analysis and never modify the source set.
              </p>
            </div>
            <div className="card-body space-y-3">
              <div className="flex items-center gap-3">
                <select
                  value={selectedParameterSetId}
                  onChange={(e) => applyParameterSet(e.target.value)}
                  className="flex-1 border border-accent-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">— None (start from empty) —</option>
                  {['org_default', 'shared', 'private'].map((visibility) =>
                    groupedParameterSets[visibility]?.length ? (
                      <optgroup key={visibility} label={VISIBILITY_LABELS[visibility]}>
                        {groupedParameterSets[visibility].map((set) => (
                          <option key={set.id} value={set.id}>
                            {set.name}
                          </option>
                        ))}
                      </optgroup>
                    ) : null
                  )}
                </select>
                {selectedParameterSetId && (
                  <button
                    type="button"
                    onClick={resetToSelectedSet}
                    className="btn-outline text-sm py-1.5 px-3 whitespace-nowrap"
                    title="Discard ephemeral edits and re-apply the selected set"
                  >
                    Reset to set
                  </button>
                )}
              </div>
              <p className="text-xs text-accent-500">
                Manage sets in{' '}
                <Link to="/app/parameter-sets" className="underline hover:text-accent-700">
                  Parameter Sets
                </Link>
                .
              </p>
            </div>
          </div>
        )}

        {/* Keywords Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Keywords</h2>
            <p className="text-sm text-accent-500 mt-1">
              Select keywords to search for in the RFP document.
            </p>
          </div>
          <div className="card-body">
            <KeywordSelector
              keywords={selectedKeywords}
              onChange={setSelectedKeywords}
              availableKeywords={availableKeywords}
            />
          </div>
        </div>

        {/* Technical Specs Section */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-accent-800">Technical Specifications</h2>
            <p className="text-sm text-accent-500 mt-1">
              Select specifications to extract from the RFP document.
            </p>
          </div>
          <div className="card-body">
            <SpecSelector
              specs={selectedSpecs}
              onChange={setSelectedSpecs}
              availableSpecs={availableSpecs}
            />
          </div>
        </div>

        {/* Extended Mode */}
        <div className="card">
          <div className="card-body">
            <label className="flex items-start gap-4 cursor-pointer">
              <input
                type="checkbox"
                checked={extendedMode}
                onChange={(e) => setExtendedMode(e.target.checked)}
                className="mt-1 w-5 h-5 text-primary-600 border-accent-300 rounded focus:ring-primary-500"
              />
              <div>
                <p className="font-medium text-accent-800">Extended Analysis Mode</p>
                <p className="text-sm text-accent-500 mt-1">
                  Uses a more powerful AI model for deeper analysis. Takes longer but provides more
                  detailed results.
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={submitting || !rfp?.filePath}
            className="btn-primary flex items-center gap-2"
          >
            {submitting ? (
              <>
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Starting Analysis...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Analyze RFP
              </>
            )}
          </button>
          <Link to={`/app/rfps/${id}`} className="btn-outline">
            Cancel
          </Link>
        </div>
      </form>

      {/* Usage Limit Error Modal */}
      {usageLimitError && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Monthly Limit Reached</h3>
              </div>

              <p className="text-gray-600 mb-4">{usageLimitError.message}</p>

              {usageLimitError.usageData && (
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm font-medium text-gray-700">Current Usage:</span>
                    <span className="text-sm font-bold text-red-600">
                      {usageLimitError.usageData.count} / {usageLimitError.usageData.limit}
                    </span>
                  </div>
                  {usageLimitError.usageData.resetsAt && (
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Resets:</span>
                      <span className="text-sm text-gray-600">
                        {new Date(usageLimitError.usageData.resetsAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm text-gray-600 mb-6">
                Please contact your administrator if you need additional analyses this month.
              </p>

              <div className="flex gap-3">
                <button onClick={closeUsageLimitModal} className="flex-1 btn-primary">
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RFPAnalyze;
